import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslocoPipe} from '@jsverse/transloco';
import {Dialog} from 'primeng/dialog';
import {Button} from 'primeng/button';
import {Textarea} from 'primeng/textarea';
import {Tooltip} from 'primeng/tooltip';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {DirectusTranslation} from '@frontend/directus';
import {
  AttributeConsistencyResolution,
  AttributeConsistencyView,
  ProjectUserControllerService,
  ProjectUserDetailResponse,
  ProjectUserStatusType,
} from '@frontend/shared-api';
import {IMPORTANCE_SCORE_LABEL_KEYS} from '../../constants/importance-score.constants';
import {
  ATTRIBUTE_CONSISTENCY_STATUS_COLORS,
  ATTRIBUTE_CONSISTENCY_STATUS_LABEL_KEYS,
} from '../../constants/attribute-consistency.constants';
import {
  AttributeConsistencyResolutionRequest,
  AttributeRatingVM,
} from './attribute-consistency-dialog.types';

const RATING_ELIGIBLE_STATUSES: ProjectUserStatusType[] = [
  ProjectUserStatusType.ACTIVE,
  ProjectUserStatusType.INVITED,
];

@Component({
  selector: 'app-attribute-consistency-dialog',
  imports: [FormsModule, TranslocoPipe, TranslateDirectusPipe, Dialog, Button, Textarea, Tooltip],
  templateUrl: './attribute-consistency-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttributeConsistencyDialogComponent {
  private readonly projectUserController = inject(ProjectUserControllerService);

  readonly visible = input.required<boolean>();
  readonly projectId = input.required<number>();
  readonly scaNumber = input<number>();
  readonly attributeTranslations = input<DirectusTranslation[]>([]);
  readonly consistency = input<AttributeConsistencyView>();
  readonly saving = input(false);

  readonly visibleChange = output<boolean>();
  readonly resolved = output<AttributeConsistencyResolutionRequest>();

  protected comment = '';

  private readonly projectUsers = signal<ProjectUserDetailResponse[]>([]);

  protected readonly homogeneity = computed<number>(() =>
    Math.round(this.consistency()?.homogeneity ?? 0),
  );

  protected readonly statusLabelKey = computed<string>(() => {
    const status = this.consistency()?.status;

    return status === undefined ? '' : ATTRIBUTE_CONSISTENCY_STATUS_LABEL_KEYS[status];
  });

  protected readonly statusColor = computed<string>(() => {
    const status = this.consistency()?.status;

    return status === undefined ? '' : ATTRIBUTE_CONSISTENCY_STATUS_COLORS[status];
  });

  protected readonly representativeScoreLabelKey = computed<string | undefined>(() => {
    const representativeScore = this.consistency()?.representativeImportanceScore;

    return representativeScore === undefined
      ? undefined
      : IMPORTANCE_SCORE_LABEL_KEYS[representativeScore];
  });

  protected readonly ratings = computed<AttributeRatingVM[]>(() => {
    const scoreByEmail = new Map(
      (this.consistency()?.ratings ?? []).map((rating) => [
        rating.createdBy ?? '',
        IMPORTANCE_SCORE_LABEL_KEYS[rating.importanceScore],
      ]),
    );

    const knownRaters = this.projectUsers()
      .filter((user) => user.status !== undefined && RATING_ELIGIBLE_STATUSES.includes(user.status))
      .map((user) => ({
        raterName: `${user.firstName} ${user.lastName}`,
        scoreLabelKey: scoreByEmail.get(user.email),
        email: user.email,
      }));

    const knownEmails = new Set(knownRaters.map((rater) => rater.email));
    const unknownRaters = [...scoreByEmail.entries()]
      .filter(([email]) => !knownEmails.has(email))
      .map(([email, scoreLabelKey]) => ({raterName: email, scoreLabelKey: scoreLabelKey}));

    return [...knownRaters, ...unknownRaters].map((rater) => ({
      raterName: rater.raterName,
      scoreLabelKey: rater.scoreLabelKey,
    }));
  });

  protected readonly hasRatings = computed<boolean>(() => this.ratings().length > 0);

  constructor() {
    effect(() => {
      if (this.visible()) {
        this.comment = this.consistency()?.comment ?? '';
      }
    });

    effect(() => {
      this.loadProjectUsers(this.projectId());
    });
  }

  onAccept(): void {
    this.emitResolution(AttributeConsistencyResolution.MANUALLY_ACCEPTED);
  }

  onRequestClarification(): void {
    this.emitResolution(AttributeConsistencyResolution.CLARIFICATION_REQUIRED);
  }

  private emitResolution(resolution: AttributeConsistencyResolution): void {
    this.resolved.emit({
      resolution: resolution,
      comment: this.comment.trim() === '' ? undefined : this.comment.trim(),
    });
  }

  private loadProjectUsers(projectId: number): void {
    this.projectUserController
      .getProjectUsersWithDetails({projectId: projectId})
      .subscribe({next: (users) => this.projectUsers.set(users)});
  }
}
